import { getDefaultState } from "../state";
import { isFunction } from "lodash";
import { deepFreeze } from "../../../util/misc";

export const SET_DARK = (state, isDark) => {
  state.dark = isDark;
  if (isDark) {
    document.documentElement.classList.add("body--dark");
  } else {
    document.documentElement.classList.remove("body--dark");
  }
};

export const SET_ABSOLUTE_TIME = (state, value) => {
  state.absoluteTime = Boolean(value);
};

export const SET_EXPAND_NAV = (state, value) => {
  state.expandNav = Boolean(value);
};

export const SHOW_MESSAGES = (state, value) => {
  state.showMessages = Boolean(value);
};

export const SET_CHANGELOG_LAST_SEEN_VERSION = (state, version) => {
  state.changelogLastSeenVersion = version || null;
};

export const SET_CHANGELOG_BASELINE_VERSION = (state, version) => {
  state.changelogBaselineVersion = version || null;
};

export const SET_MESSAGES_PROJECT = (state, id) => {
  state.messagesProjectID = id;
};

export const SCROLL_TO_MESSAGE = (state, id) => {
  state.scrollToMessageID = id;
};

export const SELECT_TOPIC = (state, id) => {
  state.selectTopicID = id || null;
};

export const START_REPLY = (state, reply) => {
  state.startReply = reply || null;
};

export const INIT = (state) => {
  state.initialized = true;
};

export const LOG_OUT = function (state) {
  // Stop all listeners
  Object.keys(state.listeners).forEach((path) => {
    state.listeners[path].unsubscribe();
  });
  state.user = null;
  state.claims = null;
  const defaultState = getDefaultState();
  Object.keys(defaultState).forEach((key) => {
    state[key] = defaultState[key];
  });
};

// Generate unique listener IDs
let listenerIdCounter = 0;
const generateListenerId = () => `listener_${++listenerIdCounter}`;

export const LISTEN = (state, payload) => {
  const { path, unsubscribe, next, error } = payload;
  const listenerId = generateListenerId();
  payload.listenerId = listenerId;
  const previous = state.listeners[path];

  if (previous) {
    // Add to existing listener
    previous.refCount++;
    if (isFunction(unsubscribe)) {
      // Chain unsubscribe functions (shouldn't normally happen for same path)
      const prevUnsubscribe = previous.unsubscribe;
      if (isFunction(prevUnsubscribe)) {
        previous.unsubscribe = () => {
          unsubscribe();
          prevUnsubscribe();
        };
      } else {
        previous.unsubscribe = unsubscribe;
      }
    }
    if (isFunction(next)) {
      previous.nextHandlers.push({ id: listenerId, handler: next });
    }
    if (isFunction(error)) {
      previous.errorHandlers.push({ id: listenerId, handler: error });
    }
  } else {
    // Create new listener
    state.listeners[path] = {
      unsubscribe,
      initialized: false,
      refCount: 1,
      nextHandlers: isFunction(next) ? [{ id: listenerId, handler: next }] : [],
      errorHandlers: isFunction(error)
        ? [{ id: listenerId, handler: error }]
        : [],
      // Computed properties for backwards compatibility
      get next() {
        return this.nextHandlers.length
          ? (data) => this.nextHandlers.forEach((h) => h.handler(data))
          : null;
      },
      get error() {
        return this.errorHandlers.length
          ? (msg) => this.errorHandlers.forEach((h) => h.handler(msg))
          : null;
      },
    };
  }

  return listenerId;
};

export const UNLISTEN = function (state, target) {
  const { path, listenerId, force } =
    typeof target === "string" ? { path: target, force: true } : target || {};
  const listener = state.listeners[path];
  if (!listener) {
    return;
  }

  if (force) {
    delete state.listeners[path];
    return true;
  }

  // Remove handlers for this listenerId
  if (listenerId) {
    listener.nextHandlers = listener.nextHandlers.filter(
      (h) => h.id !== listenerId
    );
    listener.errorHandlers = listener.errorHandlers.filter(
      (h) => h.id !== listenerId
    );
  }

  // Decrement ref count
  listener.refCount--;

  // Only delete if no more references
  if (listener.refCount <= 0) {
    delete state.listeners[path];
    return true; // Signal to action that it should unsubscribe
  }
  return false;
};

export const SET = (state, { stateKey, stateID, doc }) => {
  // Drop undefined/null docs on the floor. Without this an upstream
  // bug (e.g., Firestore listener pushing an undefined value) would
  // leave state[stateKey][id] === undefined and every downstream
  // consumer that does Object.values(...).filter(e => e.foo) would
  // crash on the undefined entry.
  if (!doc) return;
  state[stateKey][stateID || doc.stateID || doc.id] = deepFreeze(doc);
};

export const SET_MANY = (state, { stateKey, docs }) => {
  if (!docs.length) return;
  // Build a new map and assign in a single assignment so the strict-mode
  // deep+sync watcher only runs once, instead of once per doc.
  const map = { ...state[stateKey] };
  docs.forEach(({ stateID, doc }) => {
    // See SET — drop falsy docs rather than persisting undefined.
    if (!doc) return;
    map[stateID || doc.stateID || doc.id] = deepFreeze(doc);
  });
  state[stateKey] = map;
};

export const REMOVE = (state, { stateKey, stateID, id }) => {
  if (!state[stateKey]) return;
  delete state[stateKey][stateID || id];
};

export const REMOVE_MANY = (state, { stateKey, stateIDs }) => {
  if (!stateIDs?.length) return;
  // Build a new map and assign in a single assignment so the strict-mode
  // deep+sync watcher only runs once, instead of once per id.
  const map = { ...state[stateKey] };
  stateIDs.forEach((stateID) => {
    delete map[stateID];
  });
  state[stateKey] = map;
};

export const MARK_LISTENER_INITIALIZED = (state, { path }) => {
  if (state.listeners[path]) {
    state.listeners[path].initialized = true;
  }
};

export const SET_LISTENER_UNSUBSCRIBE = (state, { path, unsubscribe }) => {
  if (state.listeners[path]) {
    state.listeners[path].unsubscribe = unsubscribe;
  }
};

export const SET_CURRENT_USER = (state, { user, claims }) => {
  state.user = deepFreeze(user);
  state.claims = deepFreeze(claims);
};

// Service Center: cross-dialog handoff state. `pendingServiceQuoteIssueID`
// carries the issue a quote is being raised for; `schedulingContext` carries
// the work-order context into the calendar scheduling flow.
export const SET_PENDING_SERVICE_QUOTE_ISSUE = (state, issueID) => {
  state.pendingServiceQuoteIssueID = issueID;
};

export const SET_SCHEDULING_CONTEXT = (state, context) => {
  state.schedulingContext = context;
};

export const CLEAR_SCHEDULING_CONTEXT = (state) => {
  state.schedulingContext = null;
};
